import React from 'react';

import { cn } from './cn';
import { FormField, type FormFieldProps } from './FormField';

export interface SliderProps extends Omit<FormFieldProps, 'children'> {
  value?: number;
  defaultValue?: number;
  min?: number;
  max?: number;
  step?: number;
  onChange?: (value: number) => void;
  disabled?: boolean;
  showInput?: boolean;
  showMinMax?: boolean;
  sliderClassName?: string;
}

export const Slider = ({
  value: controlledValue,
  defaultValue = 0,
  min = 0,
  max = 100,
  step = 1,
  onChange,
  disabled,
  showInput = true,
  showMinMax = true,
  sliderClassName,
  label,
  description,
  errorMessage,
  className,
  id,
}: SliderProps) => {
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;
  const generatedId = React.useId();
  const sliderId = id || generatedId;

  const handleChange = (newValue: number) => {
    const clamped = Math.min(max, Math.max(min, newValue));
    if (!isControlled) setInternalValue(clamped);
    onChange?.(clamped);
  };

  return (
    <FormField label={label} description={description} errorMessage={errorMessage} className={className} id={sliderId}>
      <div className="flex items-center gap-3">
        {showMinMax && <span className="text-xs text-gray-400 shrink-0">{min}</span>}
        <input
          id={sliderId}
          type="range"
          min={min}
          max={max}
          step={step}
          value={currentValue}
          disabled={disabled}
          onChange={(e) => handleChange(Number(e.target.value))}
          className={cn(
            'h-2 w-full cursor-pointer appearance-none rounded-full bg-gray-200 accent-primary-500',
            disabled && 'opacity-50 cursor-not-allowed',
            sliderClassName
          )}
        />
        {showMinMax && <span className="text-xs text-gray-400 shrink-0">{max}</span>}
        {showInput && (
          <input
            type="number"
            min={min}
            max={max}
            step={step}
            value={currentValue}
            disabled={disabled}
            onChange={(e) => handleChange(Number(e.target.value))}
            className={cn(
              'w-16 rounded-md border border-gray-300 bg-surface px-2 py-1 text-center text-sm text-text-primary',
              'focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500',
              disabled && 'bg-gray-50 opacity-50 cursor-not-allowed'
            )}
          />
        )}
      </div>
    </FormField>
  );
};
